import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import Spinner from '../../common/Spinner/Spinner';
import _ from '../../../utils/lodash';

class Table extends React.Component {
  constructor(props) {
    super(props);
    // These methods are exposed through reference (passed as innerRef)
    // so that the table might be controlled from its parent component
    this.updateParams = props.updateParams;
    this.clearParams = props.clearParams;
    this.updateParamsAndGetData = props.updateParamsAndGetData;
    this.state = {
      descending: false,
      columnName: '',
    };
  }
  componentDidMount = () => {
    if (!this.props.disableGetOnLoad) {
      this.updateParamsAndGetData(this.props.initialSearchParams);
    }
  };
  componentWillReceiveProps = nextProps => {
    const { initialSearchParams: currParams, filterParams: currFilterParams } = this.props;
    const {
      initialSearchParams: newParams,
      filterParams: newFilterParams,
      searchParams,
      searchResults,
      reloadCallback,
    } = nextProps;

    if (
      reloadCallback &&
      reloadCallback.funcName !== '' &&
      reloadCallback.funcName !== this.props.reloadCallback.funcName
    ) {
      const fn = this[reloadCallback.funcName];
      if (fn && typeof fn === 'function') {
        fn(reloadCallback.funcParams);
      }
    } else if (
      searchParams.pageNumber > 0 &&
      searchResults.pages > 0 &&
      searchParams.pageNumber > searchResults.pages
    ) {
      // Update the data when the initialSearchParams are changed.
      this.updateParamsAndGetData({ pageNumber: 1 });
    } else if (nextProps.triggerReload !== this.props.triggerReload || !_.isEqual(currParams, newParams)) {
      this.updateParamsAndGetData();
    } else if (!_.isEqual(currFilterParams, newFilterParams)) {
      this.updateParamsAndGetData({ ...newParams, ...newFilterParams });
    }
  };

  setSortingDirection = sortColumn => {
    let descending = this.state.descending;
    if (this.state.columnName !== sortColumn) {
      descending = false;
    }
    const sortDirection = descending ? 'desc' : 'asc';
    this.setState({ descending: sortDirection === 'asc', columnName: sortColumn });
    return sortDirection;
  };

  getColumnName = columnHeader => {
    const sortedColumns = ['Name', 'ServiceId', 'Name', 'Name'];
    const columnHeaders = ['Name', 'Service Id', 'Referral priorities', 'Referral source'];
    const index = _.indexOf(columnHeaders, columnHeader);
    return sortedColumns[index];
  };

  handlePageChange = pageNumber => {
    this.updateParamsAndGetData({ pageNumber: pageNumber + 1 });
  };

  handlePageSizeChange = pageSize => {
    this.updateParamsAndGetData({ pageSize });
  };

  handlePageSortingChange = sortColumn => {
    const columnName = this.getColumnName(sortColumn);
    const sortDirection = this.setSortingDirection(sortColumn);
    if (columnName != null && sortDirection != null)
      this.updateParamsAndGetData({ sortColumn: columnName, sortDirection });
  };

  render() {
    const {
      searchParams,
      searchResults,
      columns,
      className,
      noDataComponent,
      updateParams,
      updateParamsAndGetData,
      clearParams,
      isFetching,
      reduxIdentifier,
      ...otherProps
    } = this.props;

    const table = (
      <ReactTable
        manual
        minRows={1}
        columns={columns}
        data={searchResults ? searchResults.items : []}
        pageSizeOptions={[1, 5, 10, 20, 25, 30, 50, 100]}
        page={searchParams.pageNumber - 1}
        pages={searchResults ? searchResults.pages : 0}
        pageSize={searchParams.pageSize}
        onPageChange={this.handlePageChange}
        onPageSizeChange={this.handlePageSizeChange}
        getTheadThProps={(state, rowInfo, column) => ({
          onClick: () => {
            this.handlePageSortingChange(column.Header);
          },
        })}
        getTrGroupProps={(state, rowInfo) => ({
          onClick: () => {
            if (rowInfo) {
              this.props.rowOnClick(rowInfo);
            }
          },
          [`${reduxIdentifier}ItemId`]: rowInfo && rowInfo.original ? rowInfo.original.id : null,
        })}
        TrGroupComponent={props => (
          <div
            tabIndex={0}
            role="link"
            id={`${reduxIdentifier}TableRow_${props[`${reduxIdentifier}ItemId`]}`}
            className="rt-tr-group"
            onClick={props.onClick}
          >
            {props.children}
          </div>
        )}
        {...otherProps}
      />
    );

    let content = null;
    if (noDataComponent) {
      if (searchResults.items && searchResults.items.length) {
        content = table;
      } else {
        content = table; // noDataComponent;
      }
    } else {
      content = table;
    }

    return (
      <div className={className}>
        {isFetching && <Spinner />}
        {content}
      </div>
    );
  }
}

Table.defaultProps = {
  searchResults: {
    isFetching: false,
    items: [],
    page: -1,
  },
  rowOnClick: () => {},
  className: '',
  searchParams: {},
  filterParams: {},
  responseObjectName: null,
  noDataComponent: null,
  disableGetOnLoad: false,
  triggerReload: false,
};

Table.propTypes = {
  // React-table column definitions, see their documentation for details.
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string,
      id: PropTypes.string,
      accessor: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
      width: PropTypes.number,
    }),
  ).isRequired,
  // True if awaiting response with table data.
  isFetching: PropTypes.bool.isRequired,
  // Used to update the search parameters for the table.
  // Accepts an object with new search parameters which is merged with existing ones.
  updateParams: PropTypes.func.isRequired,
  // Used to clear the search parameters and already existing data.
  clearParams: PropTypes.func.isRequired,
  // Combines updateParams and getData.
  updateParamsAndGetData: PropTypes.func.isRequired,
  // An object containing the current search parameters.
  // It's shape is dynamic and depends on the provided API.
  searchParams: PropTypes.shape({
    pageNumber: PropTypes.number,
    pageSize: PropTypes.number,
    sortColumn: PropTypes.string,
    sortDirection: PropTypes.string,
  }),
  // An object containing the results of a search request.
  searchResults: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    page: PropTypes.number,
  }),
  // Pass a function to fire whenever you click on the table row.
  // Accepts a single argument which contains table row data.
  rowOnClick: PropTypes.func,
  // A unique inentifier used to create a container inside redux store to maintain table state.
  reduxIdentifier: PropTypes.string.isRequired,
  // CSS classname for the div container wrapping the table.
  className: PropTypes.string,
  // Name of the object returned by the API as a response to a search request.
  // This might differ from reduxIdentifier.
  responseObjectName: PropTypes.string,
  // An object which defines the default search parameters used in the inital search
  initialSearchParams: PropTypes.shape({
    pageNumber: PropTypes.number,
    pageSize: PropTypes.number,
  }).isRequired,
  // If not null, whenever there are no records to display,
  // the table will be replaced with the provided component
  noDataComponent: PropTypes.node,
  // If true, the initial search will not be performed
  disableGetOnLoad: PropTypes.bool,
  triggerReload: PropTypes.bool,
  filterParams: PropTypes.shape({}),
};

export default Table;
